/** Redirect URL passed to Supabase / backend when requesting a password reset email. */
export function getPasswordResetRedirectUrl() {
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") {
    return `${window.location.origin}/set-new-password`;
  }
  const fromEnv =
    process.env.REACT_APP_PASSWORD_RESET_REDIRECT_URL ||
    process.env.REACT_APP_PUBLIC_SITE_URL ||
    process.env.REACT_APP_URL;
  if (fromEnv) {
    const base = String(fromEnv).replace(/\/$/, "");
    return base.includes("/set-new-password") ? base : `${base}/set-new-password`;
  }
  return `${window.location.origin}/set-new-password`;
}

/**
 * Establish a recovery session from URL params (hash, query, PKCE code, token_hash).
 * @returns {{ ok: boolean, errorMessage?: string }}
 */
export async function establishRecoverySession(auth) {
  const search = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));

  const get = (key) => search.get(key) || hash.get(key);

  const code = get("code");
  if (code) {
    const { error } = await auth.exchangeCodeForSession(code);
    if (error) {
      return { ok: false, errorMessage: error.message };
    }
    const { data: { session } } = await auth.getSession();
    if (session) return { ok: true };
  }

  const tokenHash = get("token_hash");
  const type = get("type");
  if (tokenHash && type === "recovery") {
    const { error } = await auth.verifyOtp({ token_hash: tokenHash, type: "recovery" });
    if (error) {
      return { ok: false, errorMessage: error.message };
    }
    const { data: { session } } = await auth.getSession();
    if (session) return { ok: true };
  }

  const accessToken = get("access_token");
  const refreshToken = get("refresh_token");
  const recoveryType = get("type");
  if (accessToken && refreshToken && recoveryType === "recovery") {
    const { error } = await auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken,
    });
    if (error) {
      return { ok: false, errorMessage: error.message };
    }
    return { ok: true };
  }

  // Implicit / detectSessionInUrl may have already stored the session
  const { data: { session }, error: sessionError } = await auth.getSession();
  if (sessionError) {
    return { ok: false, errorMessage: sessionError.message };
  }
  if (session) {
    return { ok: true };
  }

  return { ok: false };
}

export function mapPasswordResetRequestError(error, t) {
  if (!error) return t("password_reset_error");

  const status = error.status ?? error.statusCode ?? error.response?.status;
  const name = error.name || "";
  const msg = String(error.message || error.response?.data?.error || "").toLowerCase();

  if (status === 504 || msg.includes("504") || name === "AuthRetryableFetchError") {
    return t("password_reset_timeout");
  }
  if (
    name === "AuthRetryableFetchError" ||
    msg.includes("fetch") ||
    msg.includes("network") ||
    msg.includes("failed to fetch")
  ) {
    return t("password_reset_network_error");
  }
  if (msg.includes("invalid email")) {
    return t("password_reset_invalid_email");
  }
  if (msg.includes("rate limit")) {
    return t("password_reset_rate_limit");
  }
  return t("password_reset_error");
}
